// The user-facing sentence inside an error response body.
//
// Lives in its own module — rather than inline in `lib/api.ts` — for the same
// reason `reportingFormat.ts` does: `lib/api.ts` is the whole-app barrel and a
// plain `node` process cannot load it, so anything that stays in there cannot
// be tested by this repo's no-test-runner convention. This module imports
// nothing, so `node src/lib/api/apiError.test.ts` runs it directly.

/**
 * Pull the server's own message out of an error response body.
 *
 * FastAPI sends `{"detail": "<sentence>"}`, and the reporting compiler puts
 * real, specific text there — "unknown metric 'revenu'", "filter op 'contains'
 * is not valid on date dimension 'day'". Those sentences exist to be read by
 * the person who mistyped, so an error surface that renders the raw body shows
 * them the JSON envelope instead of the answer.
 *
 * FastAPI's own 422 puts a LIST of `{loc,msg,type}` there instead, one entry per
 * invalid field; `msg` is the sentence ("field required"), so the first entry's
 * is lifted the same way `pages/Onboard.tsx` already does it. Rendering the
 * whole list is the JSON envelope again, which is the thing this exists to stop.
 *
 * Everything that is not a usable sentence falls back to the **raw body**, never
 * to a substitute of our own: a proxy's HTML page, an empty 500, a `detail`
 * shape we do not recognise — in each case the caller is better served by
 * everything the server said than by a friendlier message we invented. The one
 * thing this must never do is throw; it runs on the path where a failure is
 * already in progress.
 */
export function apiErrorDetail(body: string): string {
  const raw = body ?? "";
  if (raw.trim() === "") return raw;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return raw; // an HTML error page, a proxy's plain text, a truncated body
  }

  // `typeof null === "object"`, and an array has no `detail`, so both the null
  // body and the list body fall through to the raw text rather than throwing.
  if (typeof parsed === "object" && parsed !== null && "detail" in parsed) {
    const detail = (parsed as { detail: unknown }).detail;
    // An empty string is a detail that says nothing — fall through to the body.
    if (typeof detail === "string" && detail.trim() !== "") return detail;
    // The 422 list. Only the first entry's `msg`, and only when it really is a
    // non-empty string: an entry with no `msg`, a list of scalars, or a list of
    // `null` all keep the raw body rather than yielding "" or "[object Object]".
    if (Array.isArray(detail) && detail.length > 0) {
      const first: unknown = detail[0];
      if (typeof first === "object" && first !== null && "msg" in first) {
        const msg = (first as { msg: unknown }).msg;
        if (typeof msg === "string" && msg.trim() !== "") return msg;
      }
    }
  }
  return raw;
}

/**
 * The sentence to SHOW for a thrown error, whatever it turns out to be.
 *
 * `ApiError` keeps the donor's `API {status} on {path}: ` prefix on `.message`
 * — that prefix is what makes a console trace or a Sentry title identifiable —
 * and exposes the server's bare sentence separately as `.detail`. A user-facing
 * surface wants the second one: Phase 1 wrote "filter op 'contains' is not valid
 * on date dimension 'day'" for the person who set that filter, and prefixing it
 * with HTTP plumbing hands them the transport when they asked about the filter.
 *
 * Structural, not `instanceof ApiError`: this module deliberately imports
 * nothing (so a bare `node` process can test it), and duck-typing also catches
 * an ApiError that crossed a bundle boundary. Anything with no usable string
 * falls back to `.message`, then to the caller's own wording — never to a
 * substitute sentence invented here.
 */
export function errorSentence(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null && "detail" in err) {
    const detail = (err as { detail: unknown }).detail;
    if (typeof detail === "string" && detail.trim() !== "") return detail;
  }
  if (err instanceof Error && err.message.trim() !== "") return err.message;
  return fallback;
}

/** Terminate a sentence that does not terminate itself, for a caller that
 * follows it with prose of its own.
 *
 * `errorSentence` deliberately returns the server's string UNCHANGED — eight
 * assertions pin that, including one that keeps a trailing space — because a
 * caller rendering the error alone wants exactly what the server said. But the
 * backend writes `detail` as a clause, not a sentence: "Brand not found:
 * does-not-exist" carries no full stop. Concatenated with the catalog card's
 * own sentence, that rendered live as
 *   "Brand not found: does-not-exist Nothing is shown rather than numbers we
 *    cannot label."
 * — two sentences fused into one line. Found by driving the page.
 *
 * Kept separate from `errorSentence` rather than folded into it, so the
 * "render the server's words verbatim" contract survives intact and only the
 * concatenating call sites opt in.
 */
export function asSentence(s: string): string {
  const t = s.trimEnd();
  return t === "" ? t : /[.!?]$/.test(t) ? t : `${t}.`;
}
